import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Table from 'react-bootstrap/Table';
import { getFirestore, Timestamp, doc, setDoc , collection, query, getDocs, where, orderBy, limit } from "@firebase/firestore";
import { app } from '../../firebase.js';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import { useNavigate } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import Pagination from 'react-bootstrap/Pagination';
import profilePictureContImage from '../../Images/defprofile.png';
import swal from 'sweetalert';
import { Link } from 'react-router-dom';
import { buildInvoiceEmail } from '../../utils/emailTemplates';

const chunkArray = (items, chunkSize) => {
    const chunks = [];

    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }

    return chunks;
};

const SearchComponent = ({ onSelectStudent }) => {
    const [searchValue, setSearchValue] = useState('');
    const [results, setResults] = useState([]);
    const searchResultsRef = useRef(null);
    const db = getFirestore(app);

    const handleSearchChange = async (event) => {
        const value = event.target.value;
        setSearchValue(value);

        if (value.trim() !== "") {
            const endValue = value + '\uf8ff';
            const q = query(collection(db, "users"), where("bio", ">=", value), where("bio", "<=", endValue));
            const querySnapshot = await getDocs(q);
            const searchResults = [];
            querySnapshot.forEach((doc) => {
                const profilePic = doc.data().profilePic || profilePictureContImage;
                const name = doc.data().bio;
                const userStatus = doc.data().userStatus;
                const userId = doc.data().userId;
                const userEmail = doc.data().userEmail;  // Ensure that email is stored in Firestore
                searchResults.push({ profilePic, bio: name, userStatus, userId,userEmail });
            });
            setResults(searchResults);
        } else {
            setResults([]);
        }
    };

    return (
        <Form.Group className="mb-3" controlId="formBasicEmail">
            <Form.Label>Search for a student</Form.Label>
            <Form.Control
                type="text"
                placeholder="Select student"
                value={searchValue}
                onChange={handleSearchChange}
            />
            {results.length > 0 && ( 
                <div className='searchResultsStud' ref={searchResultsRef}>
                    {results.map((result, index) => (
                        <div key={index} className='resultItem' onClick={() => onSelectStudent(result.userId)}>
                            <div className='resultItemDiv'>
                                <img src={result.profilePic || profilePictureContImage} alt={`${result.bio}'s profile`} />
                            </div>
                            <div>
                                <p>{result.bio}</p>
                                <p className='statusResult'>{result.userStatus}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Form.Group>
    );
};


const InvoiceComp = () => {
    const [modalShowUp, setModalShowUp] = useState(false);
    const [loading, setLoading] = useState(true);
    const [studentsList, setStudentList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 50;
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const db = getFirestore(app);
    const auth = getAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const invoiceRef = query(
                    collection(db, 'Invoices'),
                    orderBy("timeCreated", "desc")
                );
                const invoiceSnapshot = await getDocs(invoiceRef);
                const invoiceRecords = invoiceSnapshot.docs.map((invoiceDoc) => ({
                    id: invoiceDoc.id,
                    ...invoiceDoc.data()
                }));

                if (!invoiceRecords.length) {
                    setStudentList([]);
                    return;
                }

                const uniqueStudentIds = [...new Set(
                    invoiceRecords
                        .map((invoice) => invoice.studentId)
                        .filter(Boolean)
                )];

                const studentChunks = chunkArray(uniqueStudentIds, 10);
                const studentSnapshots = await Promise.all(
                    studentChunks.map((studentIds) => getDocs(
                        query(
                            collection(db, 'users'),
                            where("userId", "in", studentIds)
                        )
                    ))
                );

                const studentMap = new Map();
                studentSnapshots.forEach((snapshot) => {
                    snapshot.forEach((studentDoc) => {
                        const studentData = studentDoc.data();
                        studentMap.set(studentData.userId, studentData);
                    });
                });

                const mergedInvoiceData = invoiceRecords.map((invoice) => {
                    const studentData = studentMap.get(invoice.studentId);

                    return {
                        userId: invoice.studentId,
                        bio: studentData?.bio || 'Unknown Student',
                        course: studentData?.course || '-',
                        userStatus: studentData?.userStatus || '-',
                        userEmail: studentData?.userEmail || '',
                        phoneNumber: studentData?.phoneNumber || '',
                        income: invoice
                    };
                });

                setStudentList(mergedInvoiceData);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [db]);

    const indexOfLastRecord = useMemo(() => currentPage * recordsPerPage, [currentPage, recordsPerPage]);
    const indexOfFirstRecord = useMemo(() => indexOfLastRecord - recordsPerPage, [indexOfLastRecord]);
    const currentRecords = useMemo(() => studentsList.slice(indexOfFirstRecord, indexOfLastRecord), [studentsList, indexOfFirstRecord, indexOfLastRecord]);
    const totalPages = useMemo(() => Math.ceil(studentsList.length / recordsPerPage), [studentsList.length, recordsPerPage]);
    const totalInvoiceAmount = useMemo(() => studentsList.reduce((totals, item) => {
        const currency = item.income.currency === 'USD' ? 'USD' : 'KES';
        return {
            ...totals,
            [currency]: totals[currency] + parseFloat(item.income.amount || 0)
        };
    }, { KES: 0, USD: 0 }), [studentsList]);
    const currentPageAmount = useMemo(() => currentRecords.reduce((totals, item) => {
        const currency = item.income.currency === 'USD' ? 'USD' : 'KES';
        return {
            ...totals,
            [currency]: totals[currency] + parseFloat(item.income.amount || 0)
        };
    }, { KES: 0, USD: 0 }), [currentRecords]);
    const uniqueStudentsCount = useMemo(() => new Set(studentsList.map((item) => item.userId).filter(Boolean)).size, [studentsList]);
    const latestInvoiceRecord = studentsList[0] || null;

    const formatAmount = useCallback((value, currency = 'KES') => {
        const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
        const numericAmount = Number(value || 0);

        return `${safeCurrency} ${numericAmount.toLocaleString(safeCurrency === 'USD' ? 'en-US' : 'en-KE', {
            minimumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
            maximumFractionDigits: safeCurrency === 'USD' ? 2 : 0
        })}`;
    }, []);

    const formatCurrencyTotals = useCallback((totals) => {
        const parts = [];

        if (totals.KES) {
            parts.push(formatAmount(totals.KES, 'KES'));
        }

        if (totals.USD) {
            parts.push(formatAmount(totals.USD, 'USD'));
        }

        return parts.length ? parts.join(' / ') : formatAmount(0, 'KES');
    }, [formatAmount]);

    const formatDateTime = useCallback((timestamp) => {
        if (!timestamp?.seconds) {
            return '-';
        }

        const date = new Date(timestamp.seconds * 1000);
        return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
    }, []);

    const formatDate = useCallback((timestamp) => {
        if (!timestamp?.seconds) {
            return '-';
        }

        return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }, []);

    function generateReceiptNumber() {
        // Get the current date and time
        const now = new Date();
        
        // Extract year (last two digits) and month (two digits)
        const yearPart = now.getFullYear().toString().slice(-2);  // Get last two digits of the year
        const monthPart = String(now.getMonth() + 1).padStart(2, '0');  // Get month, pad with zero if needed
        
        // Generate a random 4-digit number
        const randomPart = Math.floor(1000 + Math.random() * 9000);  // Random number between 1000 and 9999
        
        // Combine yearPart, randomPart, and monthPart as numbers
        const receiptNumber = parseInt(`${yearPart}${randomPart}${monthPart}`, 10);
        
        return receiptNumber;
    }
      
    const handleSubmit = async () => {
        if (!selectedStudent) {
            alert("Please select a student.");
            return;
        }
        
        if (!amount || !paymentDate ) {
            alert("Please fill all fields.");
            return;
        }

        const paymentTimestamp = new Date(paymentDate).getTime();

        document.getElementById("submitBtn").style.display= "none";
        document.getElementById("spinnerbtn").style.display= "block";


        let invnumb = generateReceiptNumber()
        let userEmail = selectedStudent.userEmail
        let incomeAmount =  amount;
        let studentsUserName = selectedStudent.bio;

        const sendEmail = buildInvoiceEmail({
            studentName: studentsUserName,
            amount: incomeAmount,
            dueDate: paymentDate,
            invoiceNumber: invnumb
        });

        try {

          const newDataRef = doc(collection(db, "Invoices"));
          setDoc(newDataRef, {

                studentId: selectedStudent.userId,
                amount: amount,
                dueDate: Timestamp.fromMillis(paymentTimestamp),
                createdBy: auth.currentUser.uid,
                timeCreated: Timestamp.now(),
                paymentId:newDataRef.id,
                invoiceNumber:generateReceiptNumber()

          }).then(()=>{

            const newMail = doc(collection(db, "mail"));
            setDoc(newMail, {
              to: userEmail,
              message: {
              subject: 'Ubunifu Invoice #'+invnumb+'',
              text: 'Hi  user, ',
              html: sendEmail, 
              }
            });

            }).then(()=>{        
                sendSMS(studentsUserName, amount, paymentDate, invnumb, selectedStudent.phoneNumber);
                setModalShowUp(false);
            })

        } catch (error) {
            console.error("Error adding document: ", error);
            swal('Error!', 'An error occured!', 'error');
        }
    };

    

    const sendSMS = async (studentName, amount, paymentDate, receiptNumber, phoneNumber) => {
        const message = `Dear ${studentName}, this is a reminder to pay your fee of KES. ${amount} by ${paymentDate} to avoid learning interruption. View your invoice here https://live.ubunifucollege.com/view-invoice?${receiptNumber}. Thank you. - Ubunifu College`;
        const smsData = {
            phoneNumber: phoneNumber,
            message: message
        };

        try {
            const response = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(smsData)
            });

            const data = await response.json();
            console.log(data);
            swal("Success!", "Income submitted successfully", "success");
            window.location.reload();
            return true;
        } catch (error) {
            console.error("Error sending SMS: ", error);
            swal("Success!", "Income submitted successfully", "success");
            window.location.reload();
            return false;
        }
    };

    const MyUpdateModal = useMemo(() => (props) => {
        return (
            <Modal
                {...props}
                aria-labelledby="contained-modal-title-vcenter"
                centered>
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Send an Invoice
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SearchComponent onSelectStudent={(userId) => selectStudent(userId)} />

                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Course</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                        {selectedStudent && (
                                <tr>
                                    <td>{selectedStudent.bio}</td>
                                    <td>{selectedStudent.userStatus}</td>
                                    <td>{selectedStudent.course}</td>
                                    <td>
                                        <button className='btn btn-sm btn-primary' onClick={() => viewStudent(selectedStudent.userId)}>View</button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Selected Student</Form.Label>
                        <Form.Control type="text" value={selectedStudent ? selectedStudent.bio : ''} disabled />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Amount</Form.Label>
                        <Form.Control 
                            type="number" 
                            value={amount} 
                            onChange={(e) => setAmount(e.target.value)} 
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="formBasicEmail">
                        <Form.Label>Due Date</Form.Label>
                        <Form.Control 
                            type="datetime-local" 
                            value={paymentDate} 
                            onChange={(e) => setPaymentDate(e.target.value)} 
                        />
                    </Form.Group>


                </Modal.Body>
                <Modal.Footer>
                    <Button className='btn btn-dark' onClick={props.onHide}>Close</Button>
                    <Button 
                        variant="primary" 
                        id='submitBtn' 
                        onClick={handleSubmit}
                    >
                        Send Invoice
                    </Button> 
                    <Button 
                        variant="primary" 
                        disabled 
                        id='spinnerbtn' 
                        style={{ display: 'none' }}
                    >
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                        />
                        <span> Sending...</span>
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }, [selectedStudent, amount, paymentDate]);

    const selectStudent = useCallback(async (userId) => {
        try {
            const studentRef = query(
                collection(db, 'users'),
                where("userId", "==", userId),
                limit(1)
            );
            const studentSnapshot = await getDocs(studentRef);

            if (!studentSnapshot.empty) {
                setSelectedStudent(studentSnapshot.docs[0].data());
            }
        } catch (error) {
            console.error("Error selecting student:", error);
        }
    }, [db]);

    const viewStudent = useCallback((userId) => {
        navigate('/view-student', { state: { myData: userId } });
    }, [navigate]);

    const viewReceipt = useCallback((incomeId) => {
        window.location.href = "/view-invoice" +"?" +incomeId
       // navigate('/view-invoice', { state: { myData: incomeId } });
    }, [navigate]);

    const handlePageChange = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
    }, []);
    useEffect(() => {
        if (selectedStudent) {
            setModalShowUp(true);
        }
    }, [selectedStudent]);
    

    return (
        <div className='clientsComp invoicePage invoicePageMinimal'>
            <div className="invoiceHero">
                <div className="invoiceHeroCopy">
                    <span className="invoiceEyebrow">Finance / Billing</span>
                    <div className="invoiceHeroTitleRow">
                        <h1>Sent invoices</h1>
                    </div>
                    <p className="invoiceHeaderMeta">
                        <span>{studentsList.length} records</span>
                        <span className="invoiceInlineDot" />
                        <span>{uniqueStudentsCount} students</span>
                    </p>
                </div>
                <div className="invoiceHeroActions">
                    <Link to="/send-invoice" className="btn btn-dark">Send invoice</Link>
                </div>
            </div>

            <div className="invoiceStatsGrid">
                <div className="invoiceStatCard invoiceStatPrimary">
                    <span className="invoiceStatLabel">Total Invoiced</span>
                    <h3>{formatCurrencyTotals(totalInvoiceAmount)}</h3>
                </div>
                <div className="invoiceStatCard">
                    <span className="invoiceStatLabel">Students Covered</span>
                    <h3>{uniqueStudentsCount}</h3>
                </div>
                <div className="invoiceStatCard">
                    <span className="invoiceStatLabel">This Page Total</span>
                    <h3>{formatCurrencyTotals(currentPageAmount)}</h3>
                </div>
                <div className="invoiceStatCard">
                    <span className="invoiceStatLabel">Latest Sent Invoice</span>
                    <h3>{latestInvoiceRecord ? formatAmount(latestInvoiceRecord.income.amount, latestInvoiceRecord.income.currency) : 'No invoices'}</h3>
                    {latestInvoiceRecord && <p>{latestInvoiceRecord.bio} · {formatDate(latestInvoiceRecord.income.timeCreated)}</p>}
                </div>
            </div>

            <div className="invoiceToolbar invoiceToolbarMinimal">
                <div className="invoiceToolbarMeta">
                    <span className="invoiceToolbarText">Page {currentPage} of {totalPages || 1}</span>
                </div>
                <div className="invoiceToolbarMeta">
                    <span className="invoiceToolbarText">Showing {currentRecords.length} of {studentsList.length} records</span>
                </div>
            </div>

            {loading ? (
                <div className="invoiceStateCard">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p>Loading the latest sent invoices...</p>
                </div>
            ) : (
                <>
                    {studentsList.length === 0 ? (
                        <div className="invoiceStateCard invoiceEmptyState">
                            <h6>No invoices yet</h6>
                            <p>Once an invoice is sent, it will appear here with the newest one at the top.</p>
                            <Link to="/send-invoice" className="btn btn-dark">Send the First Invoice</Link>
                        </div>
                    ) : (
                        <>
                            <div className="invoiceTableCard">
                                <div className="invoiceTableHeader">
                                    <div>
                                        <h6>Invoice register</h6>
                                        <p>Newest invoices appear first.</p>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <Table hover className="align-middle invoiceRecordsTable">
                                        <thead>
                                            <tr>
                                                <th>Invoice Date</th>
                                                <th>Student Name</th>
                                                <th>Course</th>
                                                <th>Amount</th>
                                                <th>Due Date</th>
                                                <th>View Invoice</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentRecords.map((mystudent) => (
                                                <tr key={mystudent.income.id}>
                                                    <td>
                                                        <div className="invoiceDateCell">
                                                            <strong>{formatDateTime(mystudent.income.timeCreated)}</strong>
                                                            <span>Invoice #{mystudent.income.invoiceNumber || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td onClick={() => viewStudent(mystudent.userId)}>
                                                        <span className='viewStudentSpan invoiceStudentName'>{mystudent.bio}</span>
                                                    </td>
                                                    <td>
                                                        <span className="invoiceCoursePill">{mystudent.course}</span>
                                                    </td>
                                                    <td>
                                                        <span className="invoiceAmountText">{formatAmount(mystudent.income.amount, mystudent.income.currency)}</span>
                                                    </td>
                                                    <td>
                                                        <span className="invoiceDueDateText">{formatDate(mystudent.income.dueDate)}</span>
                                                    </td>
                                                    <td>
                                                        <button className='btn btn-sm btn-primary invoiceViewButton' onClick={() => viewReceipt(mystudent.income.invoiceNumber)}>
                                                            View invoice
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))} 
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                            <div className="invoicePaginationWrap">
                                <Pagination>
                                    {[...Array(totalPages)].map((_, index) => (
                                        <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => handlePageChange(index + 1)}>
                                            {index + 1}
                                        </Pagination.Item>
                                    ))}
                                </Pagination>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* <MyUpdateModal show={modalShowUp} onHide={() => setModalShowUp(false)} /> */}
            <MyUpdateModal key={selectedStudent?.userId || "default"} show={modalShowUp} onHide={() => setModalShowUp(false)} />

        </div>
    );
};


export default InvoiceComp
